import { isEqual } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import ApiClientAsync from "../api/ApiClientAsync";
import { getAuthToken } from "../redux/modules/auth/login/selector";

export const EMPTY_RESPONSE = {
  response: undefined,
  error: undefined,
  loading: false
};

const useGetRequest = ({
  urlPath,
  variables,
  locale,
  skipAccessCheck = false,
  clearResponseOnVariablesChange = false
}) => {
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState(EMPTY_RESPONSE.response);
  const [error, setError] = useState(EMPTY_RESPONSE.error);
  const [_variables, _setVariables] = useState(variables);
  const [fetchVersion, setFetchVersion] = useState(0);
  const hasResponseRef = useRef(false);
  const authToken = useSelector(getAuthToken);

  useEffect(() => {
    if (isEqual(variables, _variables)) {
      return;
    }

    hasResponseRef.current = false;
    if (clearResponseOnVariablesChange) {
      setResponse(EMPTY_RESPONSE.response);
      setError(EMPTY_RESPONSE.error);
      setLoading(true);
    }
    _setVariables(variables);
  }, [clearResponseOnVariablesChange, variables]);

  const refetch = useCallback(() => {
    setFetchVersion(version => version + 1);
  }, []);

  useEffect(() => {
    if (!urlPath) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const isRefetch = fetchVersion > 0 && hasResponseRef.current;

    if (!isRefetch) {
      setLoading(true);
    }

    const fetch = async () => {
      try {
        const apiClientAsync = new ApiClientAsync(locale, authToken);
        const data = await apiClientAsync.get({
          urlPath,
          variables: _variables,
          skipAccessCheck
        });

        if (cancelled) {
          return;
        }

        setResponse(data);
        setError(null);
        hasResponseRef.current = true;
      } catch (e) {
        if (cancelled) {
          return;
        }

        setError(e);
        console.error(e);
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    fetch().then();

    return () => {
      cancelled = true;
    };
  }, [urlPath, _variables, fetchVersion, locale, authToken, skipAccessCheck]);

  return { response, error, loading, refetch };
};

export default useGetRequest;
